/**
 * Les AUTRES joueurs.
 *
 * Un bonhomme est fait de quatorze maillages (un par segment): c'est quatorze
 * appels de dessin par personne affichee, sur un budget de 150 pour la scene
 * entiere (tests/e2e/performance.spec.ts). D'ou deux decisions:
 *
 * - on ne dessine que les `MAX_AUTRES_AFFICHES` plus proches (56 appels au
 *   pire), les autres restent connus mais sans corps;
 * - les voisins ne PORTENT PAS D'OMBRE. Une ombre coute un second passage,
 *   donc quatorze appels de plus par personne, ce qui doublait la facture.
 *   A la place, chacun se tient sur un petit socle a sa couleur de compte:
 *   un seul `InstancedMesh`, donc UN appel de dessin pour tout le monde, et
 *   c'est lui qui pose les bonshommes au sol au lieu de les laisser flotter.
 *
 * Aucune lumiere n'est ajoutee ici, et c'est volontaire: le nombre de lumieres
 * de la scene fait partie de la clef de cache des programmes de three.js (voir
 * CLAUDE.md). Un joueur qui arrive ne doit pas faire recompiler tous les
 * shaders.
 */
import * as THREE from 'three'
import type { Apparence } from '../../shared/types/player.ts'
import type { EtatPresence } from '../../shared/types/reseau.ts'
import { PROFONDEUR_NAGE, SEA_LEVEL } from '../../shared/constants.ts'
import { clamp } from '../../shared/sim/alea.ts'
import { MAX_AUTRES_AFFICHES, joueursLesPlusProches } from '../../shared/sim/presence.ts'
import { couleurCompte } from '../../shared/data/proprietaires.ts'
import { Bonhomme } from '../character/bonhomme.ts'
import { AnimationBonhomme, actionDepuisEtat } from '../character/animation.ts'
import { journal } from '../log/journal.ts'

/** Ce qu'on sait d'un joueur connecte, avant meme de savoir ou il est. */
export interface IdentiteVue {
  id: string
  nom: string
  compte: string
  apparence: Apparence
}

/** Ou poser l'etiquette qui porte le nom, en coordonnees du monde. */
export interface EtiquetteJoueur {
  id: string
  nom: string
  couleur: number
  x: number
  y: number
  z: number
}

interface Corps {
  bonhomme: Bonhomme
  animation: AnimationBonhomme
  /** Position precedente: c'est elle qui donne la vitesse, donc la cadence du pas. */
  precedentX: number
  precedentZ: number
  /** Enfoncement courant dans l'eau, lisse comme pour le joueur local. */
  enfoncement: number
  y: number
  /**
   * Vrai tant que le corps n'a pas ete place une premiere fois.
   *
   * Sans ce drapeau, la premiere image mesurait un deplacement depuis l'origine
   * du monde: le nouveau venu apparaissait en courant a deux cents metres par
   * seconde, le temps d'une image.
   */
  neuf: boolean
}

/** Rayon du socle de couleur, en metres. */
const RAYON_SOCLE = 0.55
/** Periode de reexamen de qui merite un corps, en secondes. */
const PERIODE_CHOIX = 0.5

export class RenduJoueurs {
  private readonly scene: THREE.Scene
  private readonly connus = new Map<string, IdentiteVue>()
  private readonly corps = new Map<string, Corps>()
  private readonly socles: THREE.InstancedMesh
  private readonly couleurTemp = new THREE.Color()
  private readonly matriceTemp = new THREE.Matrix4()
  private minuterieChoix = 0
  private affiches: string[] = []
  private etiquettesVues: EtiquetteJoueur[] = []
  private presentsVus: EtiquetteJoueur[] = []

  /**
   * Le terrain n'est plus une dependance: la hauteur de chacun arrive en
   * rappel (`solDe` de `maj`), parce qu'elle depend aussi de l'ETAGE ou l'on se
   * tient, et que ce module-ci n'a pas a connaitre les batiments.
   */
  constructor(scene: THREE.Scene) {
    this.scene = scene

    const geo = new THREE.RingGeometry(RAYON_SOCLE * 0.62, RAYON_SOCLE, 18)
    geo.rotateX(-Math.PI / 2)
    const mat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.55, depthWrite: false })
    this.socles = new THREE.InstancedMesh(geo, mat, MAX_AUTRES_AFFICHES)
    this.socles.name = 'socles_joueurs'
    this.socles.count = 0
    // Un `InstancedMesh` a zero instance coute quand meme UN appel de dessin:
    // en solo, ou en invite, il ne doit donc pas seulement etre vide, il doit
    // etre invisible. C'est ce qui laisse le budget de rendu inchange.
    this.socles.visible = false
    // Un `InstancedMesh` est dessine en entier ou pas du tout et three.js ne
    // calcule sa sphere englobante qu'une fois: avec quatre instances qui se
    // deplacent, la faire calculer serait plus cher que de la sauter.
    this.socles.frustumCulled = false
    this.socles.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    scene.add(this.socles)
  }

  /** Remplace la liste des connectes (a l'arrivee, ou apres une reprise). */
  definir(joueurs: readonly IdentiteVue[]): void {
    const gardes = new Set(joueurs.map((j) => j.id))
    for (const id of [...this.connus.keys()]) {
      if (!gardes.has(id)) this.retirer(id)
    }
    for (const j of joueurs) this.ajouter(j)
  }

  ajouter(j: IdentiteVue): void {
    const ancien = this.connus.get(j.id)
    this.connus.set(j.id, j)
    // Une apparence qui change (le joueur a repasse par le createur) demande de
    // rebatir le corps: `Bonhomme.appliquer` ne saurait pas quoi faire d'un
    // objet different, il compare champ a champ.
    if (ancien && this.corps.has(j.id)) {
      this.corps.get(j.id)?.bonhomme.appliquer(j.apparence)
    }
    this.minuterieChoix = 0
  }

  retirer(id: string): void {
    this.connus.delete(id)
    this.detruireCorps(id)
    this.affiches = this.affiches.filter((x) => x !== id)
    // Tout de suite, sans attendre la prochaine image: quand la liaison tombe,
    // `maj` ne sera plus appelee du tout et la boussole viserait un fantome.
    this.presentsVus = this.presentsVus.filter((p) => p.id !== id)
  }

  vider(): void {
    for (const id of [...this.connus.keys()]) this.retirer(id)
  }

  get nombreConnus(): number {
    return this.connus.size
  }

  /**
   * Sait-on QUI est cet identifiant ?
   *
   * Une position seule ne cree jamais d'identite ici, et tout ce qui suit
   * ecarte en silence ce qu'il ne connait pas: pas de corps, pas d'etiquette,
   * pas meme un point sur la boussole. C'est donc a l'appelant de remarquer
   * l'inconnu et de redemander la liste au serveur — il faut pour cela pouvoir
   * poser la question, sans tenir a cote une seconde liste qui finirait par
   * diverger de celle-ci.
   */
  connait(id: string): boolean {
    return this.connus.has(id)
  }

  get nombreAffiches(): number {
    return this.corps.size
  }

  /** Les etiquettes a poser, calculees par le dernier `maj`. */
  etiquettes(): readonly EtiquetteJoueur[] {
    return this.etiquettesVues
  }

  /**
   * TOUS les connectes qu'on sait situer, corps ou pas.
   *
   * `etiquettes()` ne rend que les `MAX_AUTRES_AFFICHES` plus proches, ceux qui
   * ont un corps: un nom ne se pose que sur quelqu'un de dessine. La boussole,
   * elle, cherche exactement l'inverse — celui qui est trop loin pour etre a
   * l'ecran — et son tour doit couvrir tout le monde le jour ou l'on sera dix.
   */
  presents(): readonly EtiquetteJoueur[] {
    return this.presentsVus
  }

  /**
   * Place et anime tout le monde.
   *
   * `assise` rend la hauteur d'assise d'un vehicule: un joueur a bord doit etre
   * assis DANS sa barque, pas debout dessus. Le rendu des vehicules appartient a
   * un autre module, on lui pose donc la question au lieu de dupliquer la table.
   *
   * `solDe` rend la hauteur du sol sous quelqu'un, ETAGE COMPRIS. Le moteur n'a
   * pas de verticalite: tout le monde etait pose a `terrain.hauteur(x, z)`,
   * donc un voisin qui se promene sur sa terrasse apparaissait debout sur la
   * pelouse, trois metres plus bas, en train de traverser sa propre maison. La
   * question est posee au jeu, qui connait les batiments, plutot que resolue
   * ici avec une seconde regle.
   */
  maj(
    dt: number,
    etats: ReadonlyMap<string, EtatPresence>,
    joueurX: number,
    joueurZ: number,
    assise: (idVehicule: string) => number,
    solDe: (x: number, z: number, niveau: number) => number,
  ): void {
    this.minuterieChoix -= dt
    if (this.minuterieChoix <= 0) {
      this.minuterieChoix = PERIODE_CHOIX
      this.choisirAffiches(etats, joueurX, joueurZ)
    }

    this.etiquettesVues = []
    let n = 0
    for (const id of this.affiches) {
      const c = this.corps.get(id)
      const e = etats.get(id)
      const identite = this.connus.get(id)
      if (!c || !e || !identite) continue

      const sol = solDe(e.x, e.z, e.niveau)
      const base = e.nage ? Math.max(sol, SEA_LEVEL) : sol
      const cible = e.nage ? PROFONDEUR_NAGE : 0
      c.enfoncement += clamp(cible - c.enfoncement, -2.5 * dt, 2.5 * dt)
      const hauteurAssise = e.vehicule ? assise(e.vehicule) : 0
      c.y = base - c.enfoncement + hauteurAssise

      c.bonhomme.racine.position.set(e.x, c.y, e.z)
      c.bonhomme.racine.rotation.y = e.rot
      c.bonhomme.racine.rotation.x = e.vehicule ? 0 : (c.enfoncement / PROFONDEUR_NAGE) * 0.38

      // La vitesse est MESUREE sur le deplacement reellement affiche, jamais
      // deduite de l'etat: c'est ce qui empeche les pieds de patiner quand le
      // reseau livre les positions par a-coups.
      const dx = e.x - c.precedentX
      const dz = e.z - c.precedentZ
      const vitesse = c.neuf || dt <= 0 ? 0 : Math.hypot(dx, dz) / dt
      c.precedentX = e.x
      c.precedentZ = e.z
      c.neuf = false

      const bouge = e.etat === 'marche' || e.etat === 'course'
      c.animation.jouer(e.nage && bouge ? 'nage' : actionDepuisEtat(e.etat))
      c.animation.maj(dt, vitesse)

      // Le socle se pose sur le SOL, pas sous les pieds: un nageur laisse son
      // rond a la surface, et il ne s'enfonce pas avec lui.
      this.matriceTemp.makeTranslation(e.x, base + 0.03, e.z)
      this.socles.setMatrixAt(n, this.matriceTemp)
      this.couleurTemp.setHex(couleurCompte(identite.compte))
      this.socles.setColorAt(n, this.couleurTemp)
      n++

      this.etiquettesVues.push({
        id,
        nom: identite.nom,
        couleur: couleurCompte(identite.compte),
        x: e.x,
        // Au-dessus de la tete: la taille du bonhomme plus une marge.
        y: c.y + 2.1,
        z: e.z,
      })
    }

    // Le tour de la boussole se fait sur TOUT le monde, y compris ceux qu'on
    // ne dessine pas: sans corps, on repose le point sur le sol, ce qui suffit
    // a viser et a dire s'il est deja dans le cadre.
    this.presentsVus = []
    for (const [id, e] of etats) {
      const identite = this.connus.get(id)
      if (!identite) continue
      const c = this.corps.get(id)
      this.presentsVus.push({
        id,
        nom: identite.nom,
        couleur: couleurCompte(identite.compte),
        x: e.x,
        y: (c ? c.y : solDe(e.x, e.z, e.niveau)) + 2.1,
        z: e.z,
      })
    }

    this.socles.count = n
    this.socles.visible = n > 0
    if (n > 0) {
      this.socles.instanceMatrix.needsUpdate = true
      if (this.socles.instanceColor) this.socles.instanceColor.needsUpdate = true
    }
  }

  /**
   * Qui a droit a un corps: les plus proches, dans la limite du budget.
   *
   * On ne le refait pas a chaque image. Fabriquer un bonhomme coute une
   * construction de geometrie complete: le faire soixante fois par seconde
   * parce que deux joueurs sont a egale distance serait pire que de ne pas les
   * dessiner du tout.
   */
  private choisirAffiches(
    etats: ReadonlyMap<string, EtatPresence>,
    joueurX: number,
    joueurZ: number,
  ): void {
    const positions: { id: string; x: number; z: number }[] = []
    for (const [id, e] of etats) {
      if (this.connus.has(id)) positions.push({ id, x: e.x, z: e.z })
    }
    const voulus = joueursLesPlusProches(positions, joueurX, joueurZ, MAX_AUTRES_AFFICHES)
    const ensemble = new Set(voulus)

    for (const id of [...this.corps.keys()]) {
      if (!ensemble.has(id)) this.detruireCorps(id)
    }
    for (const id of voulus) {
      if (!this.corps.has(id)) this.creerCorps(id)
    }
    this.affiches = voulus
  }

  private creerCorps(id: string): void {
    const identite = this.connus.get(id)
    if (!identite) return
    const bonhomme = new Bonhomme(identite.apparence)
    // Pas d'ombre: voir l'en-tete du fichier. C'est ce qui tient le budget.
    bonhomme.racine.traverse((o) => {
      const m = o as THREE.Mesh
      if (m.isMesh) m.castShadow = false
    })
    this.scene.add(bonhomme.racine)
    this.corps.set(id, {
      bonhomme,
      animation: new AnimationBonhomme(bonhomme),
      precedentX: 0,
      precedentZ: 0,
      enfoncement: 0,
      y: 0,
      neuf: true,
    })
    journal.debug('reseau', `corps cree pour ${identite.nom}`)
  }

  private detruireCorps(id: string): void {
    const c = this.corps.get(id)
    if (!c) return
    this.scene.remove(c.bonhomme.racine)
    c.bonhomme.dispose()
    this.corps.delete(id)
  }

  dispose(): void {
    for (const id of [...this.corps.keys()]) this.detruireCorps(id)
    this.connus.clear()
    this.scene.remove(this.socles)
    this.socles.geometry.dispose()
    ;(this.socles.material as THREE.Material).dispose()
  }
}
